import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { ColDef, GridApi, GridReadyEvent, CellClickedEvent } from 'ag-grid-community';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '../../../services/api.service';
import { FilterService } from 'src/app/services/filter.service';
import { SessionService } from 'src/app/services/session.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EditCellRendererComponent } from './edit-cell-renderer.component';
import moment from 'moment';
import { NoDataNotifierService } from 'src/app/services/no-data-notifier.service';

interface GridColumn {
  col_nm: string;
  db_col_nm: string;
  display_col_nm: string;
  col_seq_no: number;
}

// One option of the global File Type filter, mirrored as a chip in the toggle
// beside the status tabs. `reportKey` travels with the file type on this screen —
// each option carries its own report_key.
interface FileTypeOption {
  key: any;
  name: string;
  // The filter option as it arrived, so a change can be published with the value
  // under the filter's own field key.
  raw: any;
}

@Component({
    selector: 'app-data-correction-grid',
    templateUrl: './data-correction-grid.component.html',
    styleUrls: ['./data-correction-grid.component.scss'],
    standalone: false
})
export class DataCorrectionGridComponent implements OnInit, OnChanges, OnDestroy {
  static key = 100827;

  @HostBinding('class.is-fullscreen') isFullscreen = false;

  @Input() item: any;
  @Input() pageKey: any;
  @Input() heading: string = 'Fix Files';
  @Input() headerConfig: any;

  @ViewChild('dcgcontainer', { static: true }) dcgcontainer!: ElementRef;
  @ViewChild('modalTemplate') modalTemplate!: TemplateRef<any>;
  @ViewChild('changeLogTemplate') changeLogTemplate!: TemplateRef<any>;
  @Output() onSizeChange = new EventEmitter<number>();

  isActive = false;

  @HostListener('fullscreenchange', ['$event'])
  @HostListener('webkitfullscreenchange', ['$event'])
  @HostListener('mozfullscreenchange', ['$event'])
  @HostListener('MSFullscreenChange', ['$event'])
  screenChange(event: any) {
    if (!document.fullscreenElement) {
      this.closeFullscreen();
    }
  }

  private gridApi!: GridApi;
  private gridColumnApi: any;
  private overlayRef!: OverlayRef;
  private changeLogOverlayRef!: OverlayRef;

  reqSubcription: Subscription[] = [];

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private cdr: ChangeDetectorRef,
    private apiService: ApiService,
    private filterService: FilterService,
    private httpClient: HttpClient,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private noDataNotifier: NoDataNotifierService
  ) {
    this.reqSubcription.push(
      this.filterService.filterQuery.subscribe((query: any) => {
        this.isLoading = true;
        // this.getcommentsData();
        this.loadApiData();
      })
    );
  }

  rowHeight = 44;
  headerHeight = 40;
  pagination = true;
  paginationPageSize = 10;
  searchText = '';

  defaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    flex: 1,
    minWidth: 80,
    // Show only the first (general) column menu tab; hide the column-select tab.
    menuTabs: ['generalMenuTab']
  };

  private gridColumns: GridColumn[] = [];
  // Column names selected by the global "Defect Field" filter, expanded to every
  // alias each column can be named by. Used to filter ROWS — the grid always
  // shows the full column set. Empty means "no restriction".
  private selectedDefectFieldAliases = new Set<string>();
  columnDefs: ColDef[] = [];
  // Excel export column list — only the data columns returned by the grid-columns
  // API, so the export excludes internal helper fields (id, statusCode, errors,
  // fails, errorColumns, failColumns, du_universal_record_key). Passed to
  // <icon-chart> via [excelConfig].
  excelColumns: { header: string; key: string; type: string }[] = [];

  readonly tabs = [
    { label: 'Defect Records',       status: null, statuscode: 101 },
    { label: 'Saved Records',     status: 'saved', statuscode: 102 },
    { label: 'Submitted Records', status: 'submit', statuscode: 103 },
    // { label: 'Ignored Records',   status: 'ignore', statuscode: 104 },
  ];

  activeTabIndex = 0;
  // Record count per tab, in the same order as `tabs`, shown beside each label.
  tabCounts: number[] = this.tabs.map(() => 0);

  // ── File Type toggle state ──────────────────────────────────────────────────
  // One chip per File Type filter option, rendered beside the status tabs.
  fileTypeOptions: FileTypeOption[] = [];
  // The filter field key the options belong to — what a change is published
  // against. Empty when no File Type filter could be located.
  fileTypeFieldKey = '';
  // Chip counts, straight from the field-count API — the filter's own option
  // payload is not consulted for them. Keyed by file type key as a lower cased
  // string, since the filter option's key and the API's are both raw values.
  // Empty until the first response lands; a chip with no entry shows '–'.
  private fileTypeCounts = new Map<string, number>();
  // File type just clicked, highlighted until the filter change comes back round.
  private pendingFileTypeKey: string | null = null;
  // True from the moment a file type is clicked until its rows land. The rows on
  // screen still belong to the PREVIOUS file type in that window, so anything
  // counted off them reads 0 rather than carrying the old figures over.
  private fileTypeSwitchPending = false;
  // How long a clicked chip stays optimistically highlighted before falling back
  // to the filter's real state (the upstream select debounces by 500ms).
  private static readonly FILE_TYPE_APPLY_TIMEOUT_MS = 3000;
  filteredRowData: any[] = [];
  // Independent column filters driven by the ERROR / FAIL legend chips. When
  // either is active the grid shows only the columns that carry that defect
  // type (both active -> error + fail columns; neither -> all columns).
  showErrorColumns = false;
  showFailColumns = false;
  // Open error / fail totals across the rows currently shown, displayed on the
  // ERROR and FAIL legend chips. Same "n Open" figures the edit popup reports per
  // record, summed for the visible tab.
  errorCount = 0;
  failCount = 0;

  rowData: any[] = [];
  private rawRecords: any[] = [];
  commentsData: any[] = [];
  isLoading = false;
  isDataLoaded = false;
  error_name: any;

  // Modal state
  selectedRow: any = null;
  currentRecordIndex = 0;
  modalFields: any[] = [];
  isModalLoading = false;
  validationColumns: any[] = [];
  // True when at least one field has a Fixed Value (API col_value !== row value).
  hasFixedValue = false;
  // Record raw key returned by the validation API for the current record.
  recRawKey: any = null;
  // True while a save request is in progress (prevents duplicate submissions).
  isSaving = false;

  // ── View Change Log popup state ──────────────────────────────────────────────
  changeLogRows: any[] = [];
  isChangeLogLoading = false;
  changeLogError = '';
  changeLogStatusDesc = '';
  changeLogModifiedBy = '';
  changeLogRecordKey: any = null;
  private changeLogGridApi!: GridApi;

  // Change Log grid — multi-line values wrap and the row grows to fit.
  changeLogDefaultColDef: ColDef = {
    resizable: true,
    sortable: true,
    minWidth: 100,
    wrapText: true,
    autoHeight: true
  };

  changeLogColumnDefs: ColDef[] = [
    {
      headerName: 'COLUMN',
      field: 'column',
      minWidth: 160,
      cellClass: 'cl-grid-colname'
    },
    {
      headerName: 'ORIGINAL VALUE',
      field: 'oldValue',
      minWidth: 130,
      sortable: false,
      cellRenderer: (p: any) => this.changeLogValueRenderer(p, 'old')
    },
    {
      headerName: 'CORRECT VALUE',
      field: 'newValue',
      minWidth: 130,
      sortable: false,
      cellRenderer: (p: any) => this.changeLogValueRenderer(p, 'new')
    },
    {
      headerName: 'LAST UPDATE',
      field: 'lastUpdated',
      minWidth: 150,
      cellClass: 'cl-grid-muted'
    },
    {
      headerName: 'MODIFIED BY',
      field: 'modBy',
      minWidth: 160,
      cellClass: 'cl-grid-muted',
      valueGetter: (p: any) => p.data?.modBy || '--'
    },
    {
      headerName: 'INFO',
      colId: 'info',
      minWidth: 160,
      sortable: false,
      resizable: true,
      cellClass: 'cl-grid-info',
      cellRenderer: (p: any) => this.changeLogCommentRenderer(p)
    }
  ];

  // Escape user/data text before injecting into a cell renderer's innerHTML.
  private escapeHtml(value: any): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private changeLogValueRenderer(params: any, type: 'old' | 'new'): string {
    const hasVal = type === 'old' ? params.data?.hasOld : params.data?.hasNew;
    // Same MM/DD/YY treatment as the main grid — a corrected date lands here as
    // the stored timestamp, which should not read differently between the two.
    const text = this.escapeHtml(this.formatGridDate(params.value));
    if (!hasVal) {
      return `<span class="cl-value cl-empty">${text}</span>`;
    }
    const cls = type === 'old' ? 'cl-old' : 'cl-new';
    return `<span class="cl-value ${cls}">${text}</span>`;
  }

  private changeLogCommentRenderer(params: any): string {
    const hasComment = !!params.data?.comments;
    const comment = hasComment ? this.escapeHtml(params.data.comments) : 'No comment';
    return `<div class="cl-grid-comment">
        <span class="cl-grid-comment-text${hasComment ? '' : ' cl-empty'}">${comment}</span>
      </div>`;
  }

  private changeLogColumnApi: any;

  onChangeLogGridReady(params: GridReadyEvent): void {
    this.changeLogGridApi = params.api;
    this.changeLogColumnApi = (params as any).columnApi ?? params.api;
    setTimeout(() => this.changeLogColumnApi?.autoSizeAllColumns?.(), 0);
  }

  // Comment popover state
  showCommentPopover = false;
  activeCommentRow: number | null = null;
  commentAction = 'Ignore DV Check';
  commentText = '';

  // Info legend popover state
  showInfoTooltip = false;
  activeInfoRow: number | null = null;

  // Date formats recognised in a raw grid cell value. Deliberately strict and
  // separator-bearing: the grid also carries plain digit strings (quantities,
  // referral numbers, NDC numbers) that a loose parser happily reads as dates,
  // so no bare yyyyMMdd form is listed here.
  private static readonly GRID_DATE_FORMATS = [
    'YYYY-MM-DDTHH:mm:ss.SSSZ', 'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss', 'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', 'YYYY-MM-DD',
    'M/D/YYYY H:mm:ss', 'M/D/YYYY H:mm', 'M/D/YYYY', 'M-D-YYYY',
    'DD-MMM-YYYY', 'DD-MMM-YY'
  ];

  // Parsing one value against GRID_DATE_FORMATS is a strict multi-format moment
  // call — by far the most expensive thing done per cell — and the same raw
  // values repeat across rows and across every render and search pass. Each
  // distinct value is therefore parsed once and the result reused.
  private static readonly DATE_CACHE_LIMIT = 20000;
  private dateFormatCache = new Map<string, string>();

  // Render a cell value as MM/DD/YY when it is recognisably a date, so a saved
  // correction ("2026-08-03T00:00:00") and the source formats the API returns
  // ("03-FEB-25") read the same way. Anything that is not a date — including a
  // defective value the user still needs to see verbatim — is returned as-is.
  private formatGridDate(value: any): string {
    if (value === null || value === undefined) return '';
    const key = String(value);
    const cached = this.dateFormatCache.get(key);
    if (cached !== undefined) return cached;

    const formatted = this.computeGridDate(key);
    if (this.dateFormatCache.size < DataCorrectionGridComponent.DATE_CACHE_LIMIT) {
      this.dateFormatCache.set(key, formatted);
    }
    return formatted;
  }

  private computeGridDate(key: string): string {
    const raw = key.trim();
    if (raw === '') return '';
    const parsed = moment(raw, DataCorrectionGridComponent.GRID_DATE_FORMATS, true);
    return parsed.isValid() ? parsed.format('MM/DD/YY') : key;
  }

  private buildColumnDefs(columns: GridColumn[]): ColDef[] {
    const fixColumn: ColDef = {
      headerName: 'DEFECT COLUMNS',
      colId: 'fixColumns',
      minWidth: 130,
      width: 130,
      flex: 0,
      sortable: false,
      cellRenderer: (params: any) =>
        `<div style="display:flex;align-items:center;gap:10px;height:100%;">
          <span style="display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:600;color:#F59E0B;">
            <span style="width:8px;height:8px;border-radius:50%;background:#F59E0B;display:inline-block;flex-shrink:0;"></span>
            ${params.data.errors}
          </span> |
          <span style="display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:600;color:#E74C3C;">
            <span style="width:8px;height:8px;border-radius:50%;background:#E74C3C;display:inline-block;flex-shrink:0;"></span>
            ${params.data.fails}
          </span>
        </div>`
    };

    const recordKeyColumn: ColDef = {
      headerName: 'RECORD RAW KEY',
      colId: 'recordRawKey',
      field: 'du_universal_record_key',
      minWidth: 150,
      width: 150,
      flex: 0,
      sortable: true
    };

    const dataColumns: any[] = [...columns]
      .sort((a, b) => a.col_seq_no - b.col_seq_no)
      .map(col => ({
        headerName: col.display_col_nm.trim().toUpperCase(),
        field: col.db_col_nm?.toLowerCase() || 'not defined',
        minWidth: 120,
        valueFormatter: (p: any) => this.formatGridDate(p.value),
        cellStyle: (p: any) => {
          const dbField = col.db_col_nm?.toLowerCase() || 'not defined';
          const errorCols: string[] = p.data?.errorColumns ?? [];
          const failCols: string[] = p.data?.failColumns ?? [];
          if (failCols.includes(dbField)) {
            return { background: '#FFECEC', color: '#C0392B', borderRadius: '4px' };
          }
          if (errorCols.includes(dbField)) {
            return { background: '#FFF4E0', color: '#B87A00', borderRadius: '4px' };
          }
          const raw = p.data?.[dbField];
          if (raw === null || raw === undefined || raw === '') {
            return { background: 'inherit', color: 'inherit', borderRadius: '4px' };
          }
          return {};
        }
      }));

    const lastModByColumn: ColDef = {
      headerName: 'LAST MODIFIED BY',
      field: 'lst_mod_by',
      minWidth: 120,
      sortable: true
    };

    const lastModDtColumn: ColDef = {
      headerName: 'LAST MODIFIED DATE',
      field: 'lst_mod_dt',
      minWidth: 150,
      sortable: true,
      valueFormatter: (params: any) => {
        if (!params.value) return '--';
        try {
          return moment(params.value).format('YYYY-MM-DD HH:mm:ss');
        } catch {
          return params.value;
        }
      }
    };

    const actionColumn: ColDef = {
      headerName: 'ACTION',
      colId: 'edit',
      width: 50,
      flex: 0,
      sortable: false,
      resizable: false,
      pinned: 'right',
      cellRenderer: EditCellRendererComponent,
      cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '0' }
    };

    // return [fixColumn, recordKeyColumn, ...dataColumns, actionColumn];
    return [fixColumn, ...dataColumns, lastModByColumn, lastModDtColumn, actionColumn];
  }

  // ── Defect Field filter → visible rows ───────────────────────────────────────
  // The global "Defect Field" filter narrows the grid to the ROWS that carry a
  // defect on one of the selected columns; every grid column stays visible.
  // FilterService flattens a multi-select by reading the FILTER'S OWN param name
  // off each option (`options.map(o => o[paramName])`, see setFilterValue /
  // getFilter), and the Defect Field options only carry db_col_nm /
  // display_col_nm / dv_error_typ_cd. A param name outside that set therefore
  // lands in baseQuery as "undefined,undefined,…" rather than
  // "brand_nm,hcp_zip,…". So the selection is located by its VALUES — the first
  // query entry whose comma-separated tokens actually name grid columns — instead
  // of trusting a single hard-coded key. Preferred keys are probed first; the rest
  // of the query is scanned only when none of them yields a real column name.
  private static readonly DEFECT_FIELD_QUERY_KEYS = ['db_col_nm', 'display_col_nm', 'column_name', 'defect_field', 'defect_fields'];

  // Query entries that carry page/brand/date context, never a column selection.
  private static readonly NON_DEFECT_QUERY_KEYS = new Set([
    'api_key', 'brand_key', 'buffer_days', 'client_key', 'dashboard_key', 'day_of_the_week',
    'employee_key', 'file_type_dim_key', 'from_date', 'module_key', 'page_key', 'report_key',
    'report_typ', 'tbl_name_suffix', 'time_cycle', 'to_date'
  ]);

  private normalizeName(value: any): string {
    return String(value ?? '').trim().toLowerCase();
  }

  // Every name a grid column can be referred to by, so a selection still matches
  // when the filter sends display names instead of db names.
  private columnNameAliases(col: GridColumn): string[] {
    return [col?.db_col_nm, col?.col_nm, col?.display_col_nm]
      .map(v => this.normalizeName(v))
      .filter(v => v !== '');
  }

  // Split a query value into candidate column names, accepting the flattened
  // string as well as a raw option array. "undefined"/"null" tokens are the
  // fingerprint of a param-name mismatch upstream and are dropped, so they can
  // never be mistaken for a real selection.
  private toColumnNameTokens(raw: any): string[] {
    if (raw === null || raw === undefined || raw === '' || typeof raw === 'number') return [];
    const values = Array.isArray(raw) ? raw : String(raw).split(',');
    return values
      .map((v: any) => this.normalizeName(
        v !== null && typeof v === 'object' ? (v?.db_col_nm ?? v?.col_nm ?? v?.display_col_nm ?? '') : v
      ))
      .filter(name => name !== '' && name !== 'undefined' && name !== 'null');
  }

  private getSelectedDefectFields(query: any, columns: GridColumn[]): Set<string> {
    const selected = new Set<string>();
    if (!query) return selected;

    const known = new Set<string>();
    columns.forEach(c => this.columnNameAliases(c).forEach(alias => known.add(alias)));

    // Only tokens that name a real column count, so an unrelated query entry
    // cannot be mistaken for the Defect Field selection.
    const collect = (raw: any): boolean => {
      const hits = this.toColumnNameTokens(raw).filter(t => known.has(t));
      hits.forEach(t => selected.add(t));
      return hits.length > 0;
    };

    // The Defect Field multi-select publishes its own db column names, which is
    // the only source immune to the param-name flattening described above.
    if (collect(this.filterService.selectedDefectFields)) return selected;

    for (const key of DataCorrectionGridComponent.DEFECT_FIELD_QUERY_KEYS) {
      if (collect(query[key])) return selected;
    }

    for (const [key, value] of Object.entries(query)) {
      if (DataCorrectionGridComponent.NON_DEFECT_QUERY_KEYS.has(key)) continue;
      if (collect(value)) return selected;
    }

    return selected;
  }

  // Resolve the Defect Field selection to every name its columns can be referred
  // to by, so a row's defect column names match whichever naming the errors API
  // reports. Empty means "no restriction"; a selection that matches no API column
  // is also treated as no restriction rather than emptying the grid.
  private buildDefectFieldAliases(columns: GridColumn[], query: any): Set<string> {
    const aliases = new Set<string>();
    if (!columns?.length) return aliases;
    const selected = this.getSelectedDefectFields(query, columns);
    if (selected.size === 0) return aliases;
    columns
      .filter(c => this.columnNameAliases(c).some(alias => selected.has(alias)))
      .forEach(c => this.columnNameAliases(c).forEach(alias => aliases.add(alias)));
    return aliases;
  }

  // A row passes the Defect Field filter when it carries an error or a fail on at
  // least one selected column.
  private matchesDefectFieldFilter(row: any): boolean {
    if (this.selectedDefectFieldAliases.size === 0) return true;
    const defectCols: string[] = [...(row?.errorColumns ?? []), ...(row?.failColumns ?? [])];
    return defectCols.some(col => this.selectedDefectFieldAliases.has(this.normalizeName(col)));
  }

  // Build the Excel export column list from the API-provided grid columns so the
  // export contains only those columns (in col_seq_no order) and nothing else.
  private buildExcelColumns(columns: GridColumn[]): { header: string; key: string; type: string }[] {
    return [...columns]
      .sort((a, b) => a.col_seq_no - b.col_seq_no)
      .map(col => ({
        header: col.display_col_nm.trim(),
        key: col.db_col_nm?.toLowerCase()||'not defined',
        type: 'string'
      }));
  }

  private mapRowData(columns: GridColumn[], records: any[]): any[] {
    return records.map((record: any, index: number) => {
      const row: Record<string, any> = {
        id: index,
        statusCode: record['stat_cd'] ?? record['status'] ?? null,
        statusDesc: record['status_desc'] ?? null,
        du_universal_record_key: record['du_universal_record_key'] ?? null,
        errors: record['no_of_errors'] ?? 0,
        fails: record['no_of_failures'] ?? 0,
        errorColumns: this.parseColumnNames(record['col_bus_nm_errors']),
        failColumns: this.parseColumnNames(record['col_bus_nm_failures']),
        lst_mod_by: record['lst_mod_by'] ?? null,
        lst_mod_dt: record['lst_mod_dt'] ?? null
      };

      columns.forEach(col => {
        const colKey = col.col_nm.toLowerCase();
        const dbKey = col.db_col_nm?.toLowerCase()||'not defined';
        row[dbKey] = record[dbKey] ?? record[colKey] ?? null;
      });

      return row;
    });
  }

  private parseColumnNames(value: string | null): string[] {
    if (!value) return [];
    return value.split(/[,|]/).map((s: string) => s.trim().toLowerCase()).filter(s => s !== '');
  }

  // `reportNoData` is set only when a fresh API load came back empty. An empty
  // tab reached by switching tabs — or by a Defect Field / ERROR-FAIL toggle — is
  // a normal in-page state the grid already states with "No Rows To Show", so it
  // must not raise the page-level no-data notification.
  private refreshFilteredRowData(reportNoData = false): void {
    // Each tab maps to a status code (101 = Fix, 102 = Saved, 103 = Submitted).
    // Rows are additionally narrowed to the Defect Field selection, if any.
    const target = Number(this.tabs[this.activeTabIndex].statuscode);
    this.filteredRowData = this.rowData.filter(
      r => Number(r['statusCode']) === target && this.matchesDefectFieldFilter(r)
    );
    this.refreshTabCounts();
    this.refreshDefectCounts();

    if (reportNoData && !this.isLoading && this.filteredRowData.length === 0) {
      this.noDataNotifier.report('data-correction-grid');
    }
  }

  // Sum the open errors and fails over the rows on show, so the legend counts
  // always match the grid under it — tab, Defect Field and File Type included.
  private refreshDefectCounts(): void {
    let errors = 0;
    let fails = 0;
    this.filteredRowData.forEach(row => {
      errors += Number(row?.['errors']) || 0;
      fails += Number(row?.['fails']) || 0;
    });
    this.errorCount = errors;
    this.failCount = fails;
  }

  // Count the rows behind every tab in one pass. The Defect Field selection is
  // applied here too, so the active tab's count always matches the number of
  // rows shown in the grid. Mid file type switch every tab reads 0 — the rows
  // still loaded are the previous file type's.
  private refreshTabCounts(): void {
    const counts = this.tabs.map(() => 0);
    if (this.fileTypeSwitchPending) {
      this.tabCounts = counts;
      return;
    }
    this.rowData.forEach(row => {
      if (!this.matchesDefectFieldFilter(row)) return;
      const index = this.tabs.findIndex(t => Number(t.statuscode) === Number(row['statusCode']));
      if (index > -1) counts[index]++;
    });
    this.tabCounts = counts;
  }

  onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.refreshFilteredRowData();
    // Reapply the defect column filter for the new tab's data when active.
    if (this.showErrorColumns || this.showFailColumns) {
      setTimeout(() => this.updateColumnDefsForDefectFilter(), 0);
    }
  }

  // Toggle showing only the columns that carry an ERROR defect.
  toggleErrorColumns(): void {
    this.showErrorColumns = !this.showErrorColumns;
    setTimeout(() => this.updateColumnDefsForDefectFilter(), 0);
  }

  // Toggle showing only the columns that carry a FAIL defect.
  toggleFailColumns(): void {
    this.showFailColumns = !this.showFailColumns;
    setTimeout(() => this.updateColumnDefsForDefectFilter(), 0);
  }

  // Collect the defect column names for whichever legend chips are active —
  // error columns when ERROR is on, fail columns when FAIL is on.
  private getDefectColumns(): Set<string> {
    const defectCols = new Set<string>();
    this.filteredRowData.forEach(row => {
      if (this.showErrorColumns) {
        (row?.errorColumns ?? []).forEach((col: string) => defectCols.add(col));
      }
      if (this.showFailColumns) {
        (row?.failColumns ?? []).forEach((col: string) => defectCols.add(col));
      }
    });
    return defectCols;
  }

  private updateColumnDefsForDefectFilter(): void {
    const filterActive = this.showErrorColumns || this.showFailColumns;
    if (filterActive) {
      const defectCols = this.getDefectColumns();
      const filtered = this.columnDefs.filter(col => {
        const colId = col.colId;
        if (colId === 'fixColumns' || colId === 'edit') return true;
        const field = String(col.field ?? '').toLowerCase();
        // Always keep the last-modified metadata columns visible.
        if (field === 'lst_mod_by' || field === 'lst_mod_dt') return true;
        return defectCols.has(field);
      });
      this.gridApi?.setColumnDefs(filtered);
    } else {
      this.gridApi?.setColumnDefs(this.columnDefs);
    }
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && this.item?.config) {
      this.heading = this.item.config['report_heading'] || 'Fix Files';
      this.headerConfig = this.item.config['widget_header_config'] || [];
    }
  }

  closeFullscreen(): void {
    this.isFullscreen = false;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    this.isActive = false;
  }

  ngOnInit(): void {
    this.isLoading = true;
    const observer = new ResizeObserver((e: any) => {
      this.onSizeChange.emit(e[0].contentRect.height);
    });
    observer.observe(this.dcgcontainer.nativeElement);

    this.reqSubcription.push(
      this.searchInput$
        .pipe(debounceTime(250), distinctUntilChanged())
        .subscribe((value: string) => this.applySearch(value))
    );
  }

  ngOnDestroy(): void {
    this.reqSubcription.forEach((sub) => sub.unsubscribe());
    this.searchInput$.complete();
  }

  getcommentsData(): void {
    const api = this.item?.config?.['comments_api'] || {};
    if (this.filterService.isEmptyObject(this.filterService.baseQuery) === false) {
      let obj: any = { 'api_key': api.api_key };
      obj['report_typ'] = this.filterService.defaultReport_type ? this.filterService.defaultReport_type : this.filterService.report_type;
      obj['module_key'] = this.apiService.getModuleKey();

      let query = Object.assign({}, this.filterService.baseQuery, obj);
      let endpoint = this.item?.config?.['comments_api_endpoint'] || '';

      this.reqSubcription.push(
        this.httpClient.post(`${this.apiService.apiBaseUrl}/${endpoint ? endpoint : this.apiService.executionUrl}`, query).subscribe((res: any) => {
          this.commentsData = Array.isArray(res) ? res : (res?.records ?? res?.data ?? []);
          this.cdr.detectChanges();
        }, (err: any) => {
          this.commentsData = [];
          console.error('Failed to load fix-file comments', err);
        })
      );
    }
  }

  // ── File Type toggle ────────────────────────────────────────────────────────
  // The File Type filter's options are mirrored as a segmented toggle beside the
  // status tabs. The filter supplies only the options themselves — the count on
  // each chip comes from the field-count API (see fetchFileTypeCounts) — and
  // clicking one changes the REAL File Type filter: the grid then reloads
  // through the normal filterQuery path, together with the filter badge and
  // every other widget on the page.
  //
  // The field key the filter is configured with is not fixed
  // (file_type_dim_key / report_key / …), so the options are located by SHAPE —
  // any value carrying a file-type name and key — rather than by a hard-coded
  // key, the same way the Defect Field selection is resolved. The entry the
  // options are found under IS that field key, which is what identifies the
  // filter to change.
  private static readonly FILE_TYPE_NAME_KEYS = ['file_type_name', 'file_typ_nm', 'file_type', 'file_type_desc'];
  private static readonly FILE_TYPE_KEY_KEYS = ['file_type_dim_key', 'file_type_key', 'file_typ_key'];
  // Where the count sits on a field-count row; the rest are fallbacks for a
  // payload that names it differently.
  private static readonly FILE_TYPE_COUNT_KEYS = ['count', 'total_record_count', 'record_count', 'total_count', 'defect_count'];

  private firstFilled(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') return value;
    }
    return undefined;
  }

  private toFileTypeOption(raw: any): FileTypeOption | null {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const key = this.firstFilled(raw, DataCorrectionGridComponent.FILE_TYPE_KEY_KEYS);
    const name = this.firstFilled(raw, DataCorrectionGridComponent.FILE_TYPE_NAME_KEYS);
    if (key === undefined || name === undefined) return null;
    return {
      key,
      name: String(name).trim(),
      raw
    };
  }

  // Everywhere a filter's options can be found, best source first. A bucket is a
  // map of field key -> that filter's value/options.
  private fileTypeBuckets(): any[] {
    const configured: any = {};
    (this.filterService.globalFilterFields || []).forEach((field: any) => {
      if (field?.key && Array.isArray(field?.templateOptions?.options)) {
        configured[String(field.key)] = field.templateOptions.options;
      }
    });
    return [
      configured,
      this.filterService.allOptionData?.['multi'],
      this.filterService.allOptionData?.['single'],
      this.filterService.selectedItem?.['multi'],
      this.filterService.selectedItem?.['single'],
      this.filterService.model
    ].filter(bucket => bucket && typeof bucket === 'object');
  }

  // Every file type the filter knows about — not only the selected ones — plus
  // the filter field key they belong to.
  private resolveFileTypeFilter(): void {
    const options: FileTypeOption[] = [];
    const byId = new Map<string, FileTypeOption>();
    let fieldKey = '';

    this.fileTypeBuckets().forEach(bucket => {
      Object.entries(bucket).forEach(([bucketKey, value]) => {
        const candidates = Array.isArray(value) ? value : [value];
        let matched = false;
        candidates.forEach((candidate: any) => {
          const option = this.toFileTypeOption(candidate);
          if (!option) return;
          matched = true;
          const id = String(option.key);
          if (!byId.has(id)) {
            byId.set(id, option);
            options.push(option);
          }
        });
        if (matched && fieldKey === '') fieldKey = bucketKey;
      });
    });

    this.fileTypeOptions = options;
    this.fileTypeFieldKey = fieldKey;
  }

  // file_type_dim_key values the filter currently has applied. A multi-select
  // arrives flattened to "100128,100129".
  private selectedFileTypeKeys(): Set<string> {
    const keys = new Set<string>();
    const raw = this.filterService.baseQuery?.['file_type_dim_key'];
    if (raw === null || raw === undefined) return keys;
    String(raw).split(',')
      .map(v => v.trim())
      .filter(v => v !== '' && v !== 'undefined' && v !== 'null')
      .forEach(v => keys.add(v));
    return keys;
  }

  // A chip is highlighted when the live filter holds that file type. A just
  // clicked chip highlights straight away: the real filter change is debounced
  // upstream, so `pendingFileTypeKey` stands in until the reload arrives.
  isFileTypeActive(option: FileTypeOption): boolean {
    const id = String(option.key);
    if (this.pendingFileTypeKey !== null) return this.pendingFileTypeKey === id;
    return this.selectedFileTypeKeys().has(id);
  }

  // The chip's count. The ACTIVE file type is the one the grid has actually
  // loaded, so it reports the three status tabs added up — the very figures on
  // the tabs beside it, Defect Field selection included. The rest report their
  // own field-count row as it stands; a file type the response says nothing
  // about shows '–' rather than a zero.
  //
  // The live filter decides which chip is active here, not `pendingFileTypeKey`:
  // the row data still belongs to the file type the filter currently holds until
  // the reload lands.
  fileTypeCount(option: FileTypeOption): number | null {
    const id = String(option.key);
    // Mid switch the rows belong to neither chip — every one falls back to its
    // field-count figure until the new rows land.
    if (!this.fileTypeSwitchPending && this.selectedFileTypeKeys().has(id)) return this.totalTabCount;
    const count = this.fileTypeCounts.get(id.trim().toLowerCase());
    return count === undefined ? null : count;
  }

  // Records behind the three status tabs. What the active file type's chip
  // reports, so the chip and the tab counts under it can never disagree.
  get totalTabCount(): number {
    return this.tabCounts.reduce((total, count) => total + count, 0);
  }

  // Switching needs the field key the options were found under — that is what
  // names the filter to change. Without it the chips stay read-only.
  get canSwitchFileType(): boolean {
    return this.fileTypeFieldKey !== '';
  }

  // Change the REAL File Type filter. `intractiveFilter` is the channel the
  // filter badge strip already uses: the matching select control narrows itself
  // to these values and re-applies the filter, so baseQuery, the badge and every
  // widget follow — this grid included, through its filterQuery subscription.
  onFileTypeSelect(option: FileTypeOption): void {
    if (!this.canSwitchFileType || this.isFileTypeActive(option)) return;

    const id = String(option.key);
    const value = option.raw?.[this.fileTypeFieldKey] ?? option.key;
    this.pendingFileTypeKey = id;
    // The tab counts belong to the file type being left behind — blank them out
    // until the new one's rows arrive.
    this.fileTypeSwitchPending = true;
    this.refreshTabCounts();

    this.filterService.intractiveFilter.next({
      key: this.fileTypeFieldKey,
      value: String(value),
      allNewData: [],
      selectAll: false
    });

    // Nothing reaches the filter if no control owns that key. Fall back to the
    // filter's real state rather than leaving a chip highlighted for a change
    // that never happened — an applied change clears this in loadApiData first.
    setTimeout(() => {
      if (this.pendingFileTypeKey !== id) return;
      this.pendingFileTypeKey = null;
      // No reload is coming — put the counts for the rows still on screen back.
      this.fileTypeSwitchPending = false;
      this.refreshTabCounts();
      this.cdr.detectChanges();
    }, DataCorrectionGridComponent.FILE_TYPE_APPLY_TIMEOUT_MS);
  }

  loadApiData(): void {
    if (this.filterService.isEmptyObject(this.filterService.baseQuery) !== false) return;

    // The chips mirror whatever the File Type filter now holds.
    this.pendingFileTypeKey = null;
    this.resolveFileTypeFilter();

    let obj: any = {};
    obj['report_typ'] = this.filterService.report_type;
    obj['module_key'] = this.apiService.getModuleKey();

    let query = Object.assign({}, this.filterService.baseQuery, obj);

    this.fetchFileTypeCounts(query);

    this.reqSubcription.push(
      this.apiService.getFixFileGridColumns(query).subscribe((columns: any) => {
        this.gridColumns = Array.isArray(columns) ? columns : (columns?.data ?? []);
        // Every column stays in the grid (and in the Excel export); the Defect
        // Field selection only decides which ROWS are shown.
        this.selectedDefectFieldAliases = this.buildDefectFieldAliases(this.gridColumns, query);
        this.columnDefs = this.buildColumnDefs(this.gridColumns);
        this.excelColumns = this.buildExcelColumns(this.gridColumns);
        this.fetchOutstandingErrors(query);
      }, (err: any) => {
        console.error('Failed to load fix-file grid columns', err);
        // Without the column list the selection cannot be resolved — show every row.
        this.selectedDefectFieldAliases = new Set<string>();
        this.fetchOutstandingErrors(query);
      })
    );
  }

  // Counts for the File Type chips, posted with the same query the grid loads
  // with. Runs alongside the grid load rather than after it — nothing in the
  // grid depends on the counts, and nothing in the counts depends on the grid.
  private fetchFileTypeCounts(query: any): void {
    this.reqSubcription.push(
      this.apiService.getFixFileFieldCount(query).subscribe((res: any) => {
        this.fileTypeCounts = this.toFileTypeCounts(res);
        this.cdr.detectChanges();
      }, (err: any) => {
        // Show no count rather than the last file type's count.
        this.fileTypeCounts = new Map<string, number>();
        console.error('Failed to load fix-file field counts', err);
        this.cdr.detectChanges();
      })
    );
  }

  // The field-count response is one row per FILE TYPE, each already carrying
  // that file type's whole count (error_field comes back null):
  //   { source_key: 3007, file_type_key: 100129, error_field: null, count: 9 }
  // The count is taken as it stands rather than summed, so a payload that ever
  // splits a file type across rows — the old per-defect-field shape — cannot
  // inflate a chip. The response covers every file type, not only the selected
  // one, so the unselected chips keep a live count as well.
  private toFileTypeCounts(res: any): Map<string, number> {
    const counts = new Map<string, number>();
    const rows = Array.isArray(res) ? res : (res?.data ?? res?.records ?? []);
    if (!Array.isArray(rows)) return counts;

    rows.forEach((row: any) => {
      if (!row || typeof row !== 'object') return;
      const key = this.firstFilled(row, DataCorrectionGridComponent.FILE_TYPE_KEY_KEYS);
      const raw = this.firstFilled(row, DataCorrectionGridComponent.FILE_TYPE_COUNT_KEYS);
      if (key === undefined || raw === undefined) return;
      const numeric = Number(raw);
      if (isNaN(numeric)) return;
      const id = String(key).trim().toLowerCase();
      counts.set(id, numeric);
    });
    return counts;
  }

  private fetchOutstandingErrors(query: any): void {
    this.reqSubcription.push(
      this.apiService.getFixFileOutstandingErrors(query).subscribe((res: any) => {
        this.isDataLoaded = true;
        this.rawRecords = Array.isArray(res) ? res : (res?.records ?? res?.data ?? []);
        this.rowData = this.mapRowData(this.gridColumns, this.rawRecords);
        this.isLoading = false;
        // The rows on screen are the new file type's from here on.
        this.fileTypeSwitchPending = false;
        this.refreshFilteredRowData(true);
        this.reapplyDefectColumnFilter();
        this.error_name = '';
        this.cdr.detectChanges();
      }, (err: any) => {
        this.rawRecords = [];
        this.rowData = [];
        this.fileTypeSwitchPending = false;
        this.refreshFilteredRowData();
        this.isLoading = false;
        this.error_name = friendlyApiErrorMessage(err);
        this.cdr.detectChanges();
      })
    );
  }

  // The ERROR / FAIL chips filter by column, so an active selection has to be
  // re-applied against the columns the file type just loaded declares.
  private reapplyDefectColumnFilter(): void {
    if (!this.showErrorColumns && !this.showFailColumns) return;
    setTimeout(() => this.updateColumnDefsForDefectFilter(), 0);
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridColumnApi = (params as any).columnApi ?? params.api;
    setTimeout(() => this.gridColumnApi?.autoSizeAllColumns?.(), 0);
  }

  onCellClicked(e: CellClickedEvent): void {
    if (e.column.getColId() === 'edit') {
      this.selectedRow = { ...e.data };
      this.currentRecordIndex = this.filteredRowData.findIndex(r => r['id'] === e.data.id);
      this.showCommentPopover = false;
      this.showInfoTooltip = false;
      this.loadValidationDetails();
      this.openOverlay();
    }
  }

  private openOverlay(): void {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dcg-overlay-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
    const portal = new TemplatePortal(this.modalTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);
    this.overlayRef.backdropClick().subscribe(() => this.requestCloseModal());
  }

  // True when the open record has edited Fix values that have not been saved.
  get hasUnsavedChanges(): boolean {
    return this.modifiedFields.length > 0;
  }

  // Warn before discarding unsaved Fix edits; returns true when it's safe to leave.
  private confirmDiscardIfDirty(): boolean {
    if (!this.hasUnsavedChanges) { return true; }
    return window.confirm('You have unsaved changes. Do you want to discard them without saving?');
  }

  // Close requested by the user (close button / backdrop) — guard unsaved edits.
  requestCloseModal(): void {
    if (!this.confirmDiscardIfDirty()) { return; }
    this.closeModal();
  }

  closeModal(): void {
    this.overlayRef?.detach();
    this.overlayRef?.dispose();
    this.selectedRow = null;
    this.showCommentPopover = false;
    this.showInfoTooltip = false;
  }

  openPrevRecord(): void {
    if (this.currentRecordIndex > 0) {
      if (!this.confirmDiscardIfDirty()) { return; }
      this.currentRecordIndex--;
      this.selectedRow = { ...this.filteredRowData[this.currentRecordIndex] };
      this.showCommentPopover = false;
      this.loadValidationDetails();
      this.cdr.detectChanges();
    }
  }

  openNextRecord(): void {
    if (this.currentRecordIndex < this.filteredRowData.length - 1) {
      if (!this.confirmDiscardIfDirty()) { return; }
      this.currentRecordIndex++;
      this.selectedRow = { ...this.filteredRowData[this.currentRecordIndex] };
      this.showCommentPopover = false;
      this.loadValidationDetails();
      this.cdr.detectChanges();
    }
  }

  // Fetch the original validation values for the selected record and build the
  // Actual Value vs Fixed Value comparison once the response arrives.
  loadValidationDetails(): void {
    this.modalFields = [];
    this.validationColumns = [];
    this.hasFixedValue = false;
    this.recRawKey = this.selectedRow?.['du_universal_record_key'] ?? null;
    if (!this.selectedRow) { return; }

    const key = this.selectedRow['du_universal_record_key'];
    if (key === null || key === undefined || key === '') {
      this.buildModalFields([]);
      return;
    }

    const recordKey = (typeof key === 'string' && /^\d+$/.test(key)) ? Number(key) : key;

    this.isModalLoading = true;
    this.cdr.detectChanges();

    this.reqSubcription.push(
      this.apiService.getFixFileErrorByKey({
        du_universal_record_key: recordKey,
        brand_key: this.filterService.baseQuery?.brand_key,
        file_type_dim_key: this.filterService.baseQuery?.file_type_dim_key,
        source_key: this.filterService.baseQuery?.source_key
      }).subscribe((res: any) => {
        this.recRawKey = res?.rec_raw_key ?? this.selectedRow?.['du_universal_record_key'] ?? null;
        this.validationColumns = Array.isArray(res?.columns) ? res.columns : [];
        this.buildModalFields(this.validationColumns);
        this.isModalLoading = false;
        this.cdr.detectChanges();
      }, (err: any) => {
        this.validationColumns = [];
        this.buildModalFields([]);
        this.isModalLoading = false;
        this.cdr.detectChanges();
        console.error('Failed to load validation details', err);
      })
    );
  }

  // Map each validation column from the API against the selected row to derive
  // the Actual Value, Fixed Value and per-field legend information.
  private buildModalFields(columns: any[]): void {
    if (!this.selectedRow) { this.modalFields = []; return; }
    const r = this.selectedRow as Record<string, any>;
    const errorCols: string[] = r['errorColumns'] ?? [];
    const failCols: string[] = r['failColumns'] ?? [];

    this.modalFields = (columns || []).map((col: any, idx: number) => {
      const dbCol = String(col?.db_col_nm ?? '').toLowerCase();

      // Data type drives which Fix Record input is rendered (I=Number, C=Text, D=Date).
      const dataType = this.normalizeDataType(col?.col_data_typ);

      // Actual Value — original value returned by the API.
      const actualValue = col?.col_value ?? '';

      // Current value in the selected record; "-" when the field is absent.
      const hasValue = !!dbCol && r[dbCol] !== null && r[dbCol] !== undefined;
      const rowValue = hasValue ? String(r[dbCol]) : '';

      // Case 1: values identical -> Fixed Value empty. Case 2: differ -> row value.
      const isSame = String(actualValue) === rowValue;
      let fixValue = isSame ? '' : rowValue;
      // Date columns start empty like Text/Number — the user must explicitly pick
      // a date. Only when a real corrected value already exists (Saved/Submitted
      // records) do we normalise it to the yyyy-MM-dd the native picker needs,
      // dropping any stored time; an untouched field is left blank rather than
      // defaulting to a date.
      if (dataType === 'D' && fixValue !== '') {
        fixValue = this.toDateInputValue(fixValue);
      }
      // A real correction only counts when the field exists on the row and differs.
      const corrected = hasValue && !isSame;

      let status = '';
      if (failCols.includes(dbCol)) status = 'fail';
      else if (errorCols.includes(dbCol)) status = 'error';
      else if (String(col?.dv_error_typ_cd ?? '').toUpperCase() === 'F') status = 'fail';
      else status = 'error';

      return {
        id: idx,
        column: String(col?.display_col_nm ?? col?.db_col_nm ?? '').trim(),
        dbCol,
        dataType,
        originalValue: actualValue,
        fixValue,
        // Picker view model for D columns; null for every other data type.
        fixDate: dataType === 'D' ? this.toPickerDate(fixValue) : null,
        // Baseline snapshot of the prefilled Fix Record value — used to detect
        // whether the user has actually edited the field in this session, as
        // opposed to it merely differing from the Original Record value (which
        // is always true for already-corrected Saved/Submitted records).
        initialFixValue: fixValue,
        corrected,
        comments: col?.comments ?? col?.comment ?? col?.col_comment ?? '',
        status,
        actualDefect: col?.dv_error_msg ?? '',
        validationCheck: col?.dv_cd_desc ?? ''
      };
    });

    // Comments/Submit are allowed only when at least one field has a Fixed Value.
    this.hasFixedValue = this.modalFields.some(f => f.corrected);
  }

  // ── Fix Record input typing & validation ─────────────────────────────────────
  // Normalise the API's col_data_typ into one of the supported input types.
  // I -> Number, D -> Date, anything else (incl. C) -> Text.
  private normalizeDataType(raw: any): 'I' | 'C' | 'D' {
    const code = String(raw ?? '').trim().toUpperCase();
    if (code === 'I') return 'I';
    if (code === 'D') return 'D';
    return 'C';
  }

  // Convert an arbitrary date string into the yyyy-MM-dd value a native
  // <input type="date"> expects; returns '' when it cannot be parsed. Any time
  // part in the source is dropped — the Fix Record picker is date-only and the
  // payload re-attaches a zeroed time (see toPayloadValue).
  private toDateInputValue(value: any): string {
    if (value === null || value === undefined || String(value).trim() === '') return '';
    const pad = (n: number) => String(n).padStart(2, '0');

    // What the Material picker binds to.
    if (value instanceof Date) {
      return isNaN(value.getTime())
        ? ''
        : `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
    }

    const raw = String(value).trim();

    // Already in yyyy-MM-dd, optionally followed by a T/space time part we drop.
    let m = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
    if (m) {
      return `${m[1]}-${m[2]}-${m[3]}`;
    }

    // MM/DD/YYYY (or MM-DD-YYYY), again with any trailing time part dropped.
    m = raw.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})(?:[ T].*)?$/);
    if (m) {
      return `${m[3]}-${pad(+m[1])}-${pad(+m[2])}`;
    }

    // Fallback to the JS parser for any other recognised format.
    const d = new Date(raw);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  // Local-midnight Date for the picker to bind to. Built from the parts rather
  // than parsed from the ISO string, which would be read as UTC and can land on
  // the previous day west of Greenwich.
  private toPickerDate(value: any): Date | null {
    const iso = this.toDateInputValue(value);
    if (iso === '') return null;
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  // A date picked in the calendar; fixValue stays the string everything else reads.
  onDatePicked(field: any): void {
    field.fixValue = this.toDateInputValue(field?.fixDate);
  }

  // The calendar has no clear of its own, so the field carries one.
  clearDate(field: any): void {
    if (!field) return;
    field.fixDate = null;
    field.fixValue = '';
  }

  // The Fix Record picker for D columns shows a date only, but the API still
  // expects a timestamp, so a saved date is sent with its time zeroed out.
  private static readonly PAYLOAD_TIME_SUFFIX = 'T00:00:00';

  // The value sent to the API for one field: D columns get the picked date plus
  // the zeroed time; every other data type is sent exactly as it was typed.
  private toPayloadValue(field: any): string {
    const value = String(field?.fixValue ?? '').trim();
    if (field?.dataType !== 'D' || value === '') return value;
    const datePart = this.toDateInputValue(value);
    if (datePart === '') return '';
    return datePart + DataCorrectionGridComponent.PAYLOAD_TIME_SUFFIX;
  }

  // Block any keystroke that would produce a non-numeric Fix value for I columns.
  onNumberKeydown(event: KeyboardEvent): void {
    const allowed = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End',
      'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) return;
    // Permit digits, a single optional sign and decimal point.
    if (!/^[0-9.\-]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  // Per-field validation message; '' when the current Fix value is acceptable.
  getFieldError(field: any): string {
    const value = String(field?.fixValue ?? '').trim();
    if (value === '') return '';
    switch (field?.dataType) {
      case 'I':
        return /^-?\d+(\.\d+)?$/.test(value) ? '' : 'Enter a valid number.';
      case 'D':
        return isNaN(new Date(value).getTime()) ? 'Enter a valid date.' : '';
      default:
        return '';
    }
  }

  // True when any field currently holds a value that fails its type validation.
  private hasValidationErrors(): boolean {
    return this.modalFields.some(f => !!this.getFieldError(f));
  }

  toggleCommentPopover(rowId: number, event: Event): void {
    event.stopPropagation();
    if (this.activeCommentRow === rowId && this.showCommentPopover) {
      this.showCommentPopover = false;
      this.activeCommentRow = null;
    } else {
      this.activeCommentRow = rowId;
      this.showCommentPopover = true;
      this.showInfoTooltip = false;
      this.activeInfoRow = null;
    }
  }

  closeCommentPopover(): void {
    this.showCommentPopover = false;
    this.activeCommentRow = null;
  }

  saveComment(): void {
    this.showCommentPopover = false;
    this.activeCommentRow = null;
  }

  toggleInfoTooltip(event: Event, rowId?: number): void {
    event.stopPropagation();
    const id = rowId ?? null;
    if (this.showInfoTooltip && this.activeInfoRow === id) {
      this.showInfoTooltip = false;
      this.activeInfoRow = null;
    } else {
      this.showInfoTooltip = true;
      this.activeInfoRow = id;
      this.showCommentPopover = false;
      this.activeCommentRow = null;
    }
  }

  gridContext = { componentParent: this };

  onEditClick(data: any): void {
    this.selectedRow = { ...data };
    this.currentRecordIndex = this.filteredRowData.findIndex(r => r['id'] === data.id);
    this.showCommentPopover = false;
    this.showInfoTooltip = false;
    this.loadValidationDetails();
    this.openOverlay();
  }

  filterValue: string = '';

  // The searchable text of a row — every displayed column, each value carried
  // both as stored and as the grid shows it — is costly to assemble and does not
  // change while the user types. It is built once per row and reused until the
  // row object or the set of displayed columns changes. The rows are replaced
  // wholesale on every reload, so the WeakMap drops stale entries by itself.
  private rowSearchTextCache = new WeakMap<object, { columns: string; text: string }>();
  private displayedSearchFields: string[] = [];
  private displayedSearchFieldsKey = '';

  // ag-Grid calls this once at the start of a filter pass, before
  // doesExternalFilterPass runs over the rows — the one place the column list
  // behind the row text can be refreshed without paying for it per row.
  isExternalFilterPresent = (): boolean => {
    const present = !!this.filterValue && String(this.filterValue).trim().length > 0;
    if (present) this.refreshDisplayedSearchFields();
    return present;
  };

  doesExternalFilterPass = (node: any): boolean => {
    if (!this.filterValue || !node?.data) return true;
    const search = String(this.filterValue).toLowerCase().trim();
    if (!search) return true;
    return this.rowSearchText(node.data).includes(search);
  };

  private refreshDisplayedSearchFields(): void {
    const cols = this.gridColumnApi?.getAllDisplayedColumns?.() || [];
    this.displayedSearchFields = cols
      .map((col: any) => col.getColDef?.()?.field)
      .filter((field: any) => !!field);
    this.displayedSearchFieldsKey = this.displayedSearchFields.join('|');
  }

  private rowSearchText(data: any): string {
    const cached = this.rowSearchTextCache.get(data);
    if (cached && cached.columns === this.displayedSearchFieldsKey) return cached.text;

    const parts: string[] = [];
    for (const field of this.displayedSearchFields) {
      const v = data[field];
      if (v == null) continue;
      // Match the stored value AND what the grid actually shows, so a date can
      // be searched either as stored or as it appears on screen (MM/DD/YY).
      const raw = String(v);
      const shown = this.formatGridDate(v);
      parts.push(shown === raw ? raw : `${raw} ${shown}`);
    }

    const text = parts.join(' ').toLowerCase();
    this.rowSearchTextCache.set(data, { columns: this.displayedSearchFieldsKey, text });
    return text;
  }

  getContextMenuItems = (params: any): any[] => {
    const cellValue = params.value != null ? String(params.value) : '';
    return [
      {
        name: 'Copy Cell',
        action: () => navigator.clipboard.writeText(cellValue),
        icon: '<span style="font-size:14px;">📋</span>'
      },
      {
        name: 'Copy Row',
        action: () => {
          if (!params.node?.data) return;
          const row = Object.entries(params.node.data)
            .filter(([k]) => !['id', 'statusCode', 'statusDesc', 'errors', 'fails', 'errorColumns', 'failColumns'].includes(k))
            .map(([, v]) => (v == null ? '' : String(v)))
            .join('\t');
          navigator.clipboard.writeText(row);
        },
        icon: '<span style="font-size:14px;">📄</span>'
      },
      'separator',
      'copy',
      'copyWithHeaders',
    ];
  };

  // The search box emits on every keystroke and each pass re-filters every row,
  // so the raw stream is debounced before it reaches the grid — one filter pass
  // per pause in typing instead of one per character.
  private searchInput$ = new Subject<string>();

  onSearch(event: any): void {
    this.searchInput$.next(event ?? '');
  }

  private applySearch(value: string): void {
    this.filterValue = value;
    this.gridApi?.onFilterChanged();
  }

  fullscreenChanges(e: any): void {
    this.isFullscreen = e;
    this.isActive = e;
  }
  ignoreRecord(): void {}

  // ── View Change Log ──────────────────────────────────────────────────────────
  viewChangeLog(): void {
    const key = this.recRawKey ?? this.selectedRow?.['du_universal_record_key'];
    if (key === null || key === undefined || String(key).trim() === '') {
      this.notify('Unable to load change log because the record key is missing.', 'error');
      return;
    }

    this.changeLogRecordKey = key;
    this.changeLogRows = [];
    this.changeLogError = '';
    this.changeLogStatusDesc = '';
    this.changeLogModifiedBy = '';
    this.openChangeLogOverlay();
    this.loadChangeLog();
  }

  loadChangeLog(): void {
    const key = this.changeLogRecordKey;
    if (key === null || key === undefined || String(key).trim() === '') { return; }

    const recordKey = (typeof key === 'string' && /^\d+$/.test(key)) ? Number(key) : key;

    this.isChangeLogLoading = true;
    this.changeLogError = '';
    this.cdr.detectChanges();

    this.reqSubcription.push(
      this.apiService.getFixFileChangeAudit({
        rec_raw_key: recordKey,
        brand_key: this.filterService.baseQuery?.brand_key,
        source_key: this.filterService.baseQuery?.source_key,
        file_type_dim_key: this.filterService.baseQuery?.file_type_dim_key
      }).subscribe((res: any) => {
        const records = Array.isArray(res) ? res : (res?.records ?? res?.data ?? res?.columns ?? []);
        this.buildChangeLogRows(records);
        this.isChangeLogLoading = false;
        this.cdr.detectChanges();
      }, (err: any) => {
        this.changeLogRows = [];
        this.isChangeLogLoading = false;
        this.changeLogError = friendlyApiErrorMessage(err, 'Unable to load the change log. Please try again.');
        this.cdr.detectChanges();
      })
    );
  }

  // Map the audit response dynamically and sort by mod_dt (latest first).
  private buildChangeLogRows(records: any[]): void {
    const rows = (records || []).map((rec: any) => ({
      column: this.displayOrDash(rec?.field_nm),
      oldValue: this.displayOrDash(rec?.old_value),
      newValue: this.displayOrDash(rec?.new_value),
      hasOld: this.hasValue(rec?.old_value),
      hasNew: this.hasValue(rec?.new_value),
      lastUpdated: this.formatDate(rec?.mod_dt),
      comments: rec?.comments ?? '',
      modBy: rec?.mod_by ?? '',
      statusDesc: rec?.change_stat_desc ?? '',
      sortKey: this.toTime(rec?.mod_dt)
    }));

    rows.sort((a, b) => b.sortKey - a.sortKey);
    this.changeLogRows = rows;

    // Header/footer meta — take from the most recent record.
    const latest = rows[0];
    this.changeLogStatusDesc = latest?.statusDesc || '';
    this.changeLogModifiedBy = latest?.modBy || '';
  }

  private hasValue(v: any): boolean {
    return v !== null && v !== undefined && String(v).trim() !== '';
  }

  private displayOrDash(v: any): string {
    return this.hasValue(v) ? String(v) : '--';
  }

  private toTime(value: any): number {
    if (!this.hasValue(value)) return 0;
    const t = new Date(value).getTime();
    return isNaN(t) ? 0 : t;
  }

  // Format mod_dt as "yyyy-MM-dd hh:mm AM/PM"; returns "--" when unavailable.
  private formatDate(value: any): string {
    if (!this.hasValue(value)) return '--';
    const d = new Date(value);
    if (isNaN(d.getTime())) return String(value);
    const pad = (n: number) => String(n).padStart(2, '0');
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    let hours = d.getHours();
    const minutes = pad(d.getMinutes());
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${year}-${month}-${day} ${pad(hours)}:${minutes} ${ampm}`;
  }

  private openChangeLogOverlay(): void {
    this.changeLogOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'dcg-overlay-backdrop',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block()
    });
    const portal = new TemplatePortal(this.changeLogTemplate, this.viewContainerRef);
    this.changeLogOverlayRef.attach(portal);
    this.changeLogOverlayRef.backdropClick().subscribe(() => this.closeChangeLog());
  }

  closeChangeLog(): void {
    this.changeLogOverlayRef?.detach();
    this.changeLogOverlayRef?.dispose();
    this.changeLogRows = [];
    this.changeLogError = '';
  }

  // Save persists the record as "Saved" (102) and closes the popup.
  save(): void { this.saveRecord('102'); }
  // Submit persists the record as "Submitted" (103) and closes the popup.
  submit(): void { this.saveRecord('103'); }

  // A field counts as modified when its live Fix Record value has actually been
  // edited by the user, i.e. it differs from the value it was prefilled with —
  // NOT from the Original Record value, which already differs for records that
  // were corrected in a prior session (Saved/Submitted), causing a false
  // "unsaved changes" prompt on simple open/close with no edits.
  private isFieldModified(field: any): boolean {
    const fixed = String(field?.fixValue ?? '').trim();
    const baseline = String(field?.initialFixValue ?? '').trim();
    if (fixed === '') return false;
    // Date fields are prefilled from the original value in a normalised format,
    // so compare both sides on the same normalised scale to avoid false positives.
    if (field?.dataType === 'D') {
      return this.toDateInputValue(fixed) !== this.toDateInputValue(baseline);
    }
    return fixed !== baseline;
  }

  // A field carries a real Fix value when its Fix Record input is non-empty and
  // differs from the Original Record value — this holds for values corrected in
  // the current session AND for values already persisted in a prior Save. Used
  // by Submit so a Saved record can be submitted without re-editing its values.
  private hasRealFixValue(field: any): boolean {
    const fixed = String(field?.fixValue ?? '').trim();
    const original = String(field?.originalValue ?? '').trim();
    if (fixed === '') return false;
    if (field?.dataType === 'D') {
      return this.toDateInputValue(fixed) !== this.toDateInputValue(original);
    }
    return fixed !== original;
  }

  // List of modified fields based on the live Fix Record inputs.
  get modifiedFields(): any[] {
    return this.modalFields.filter(f => this.isFieldModified(f));
  }

  // Save is enabled when there is at least one modified field and no save is in
  // progress. Comments are optional.
  get canSave(): boolean {
    if (this.isSaving) return false;
    if (this.hasValidationErrors()) return false;
    return this.modifiedFields.length > 0;
  }

  // Submit validation follows two scenarios:
  // Scenario 1: If FAIL fields (blocking) exist, ALL must be fixed. ERRORs (non-blocking) can be excluded.
  // Scenario 2: If only ERROR fields exist (no FAILs), at least ONE ERROR must be fixed.
  // In the Fix Records and Saved Records tabs a partial fix must be Saved, not Submitted.
  // Submitted records are view-only and cannot be resubmitted.
  get canSubmit(): boolean {
    if (this.isSaving) return false;
    if (this.hasValidationErrors()) return false;
    if (this.isFixRecord || this.isSavedRecord) {
      // Separate FAIL fields (blocking) from ERROR fields (non-blocking)
      const failFields = this.modalFields.filter(f => f.status === 'fail');
      const errorFields = this.modalFields.filter(f => f.status === 'error');
      
      // Scenario 1: If there are FAIL fields, ALL must have a fix value
      if (failFields.length > 0) {
        return failFields.every(f => String(f?.fixValue ?? '').trim() !== '');
      }
      
      // Scenario 2: If there are only ERROR fields (no FAILs), at least ONE must have a fix value
      if (errorFields.length > 0) {
        return errorFields.some(f => String(f?.fixValue ?? '').trim() !== '');
      }
      
      // No fields to fix at all
      return false;
    }
    return this.modifiedFields.length > 0;
  }

  private notify(message: string, type: 'success' | 'error'): void {
    this.snackBar.open(message, '', {
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      duration: 3000,
      panelClass: [type === 'success' ? 'success-class' : 'error-class']
    });
  }

  // Build the payload dynamically and POST it once every validation passes.
  // `status` is "102" for Save, "103" for Submit.
  private saveRecord(status: string): void {
    if (this.isSaving) return;

    const recRawKey = this.recRawKey ?? this.selectedRow?.['du_universal_record_key'];
    const modifiedBy = String(this.sessionService.getMainUsername() ?? this.sessionService.getUsername() ?? '').trim();
    const sessionId = this.sessionService.getLogSession()?.session_id;

    // Field selection differs by action:
    // • Save (102)  → only the fields the user edited in THIS session.
    // • Submit (103) → every field that carries a real Fix value (differs from
    //   the Original Record), even those already persisted in a prior Save. This
    //   lets a Saved record be submitted without re-editing its values.
    const isSubmit = status === '103';
    const modified = isSubmit
      ? this.modalFields.filter(f => this.hasRealFixValue(f))
      : this.modalFields.filter(f => this.isFieldModified(f));
    if (modified.length === 0) {
      this.notify('No fixed values were found. Nothing to save.', 'error');
      return;
    }

    // Submitting a Fix Records-tab entry requires validation based on field types:
    // Scenario 1: If FAIL fields exist, ALL must have a Fix Record value.
    // Scenario 2: If only ERROR fields exist (no FAILs), at least ONE must have a Fix Record value.
    // A partial fix must be Saved instead.
    if (status === '103' && this.isFixRecord) {
      const failFields = this.modalFields.filter(f => f.status === 'fail');
      const errorFields = this.modalFields.filter(f => f.status === 'error');
      
      // Scenario 1: If there are FAIL fields, all must be fixed
      if (failFields.length > 0) {
        if (failFields.some(f => String(f?.fixValue ?? '').trim() === '')) {
          this.notify('Please provide a Fix Record value for every Failure row before submitting.', 'error');
          return;
        }
      }
      // Scenario 2: If there are only ERROR fields (no FAILs), at least one must be fixed
      else if (errorFields.length > 0) {
        if (!errorFields.some(f => String(f?.fixValue ?? '').trim() !== '')) {
          this.notify('Please fix at least one Error before submitting.', 'error');
          return;
        }
      }
    }

    // Reject the save when any Fix value does not match its column data type.
    const invalid = modified.find(f => !!this.getFieldError(f));
    if (invalid) {
      this.notify(`${invalid.column}: ${this.getFieldError(invalid)}`, 'error');
      return;
    }

    // Build the data array — trim every value and reject duplicates by db_col_nm.
    const seen = new Set<string>();
    const data: any[] = [];
    for (const f of modified) {
      const dbCol = String(f.dbCol ?? '').trim();
      const colValue = String(f.originalValue ?? '').trim();
      // D columns are sent as the picked date with a 00:00:00 time appended.
      const fixedValue = this.toPayloadValue(f);
      const comments = String(f.comments ?? '').trim();

      if (!dbCol || fixedValue === '' || fixedValue === colValue) {
        this.notify('Unable to save because required information is missing.', 'error');
        return;
      }
      if (seen.has(dbCol)) {
        this.notify('Duplicate modified fields were found. Please review the changes.', 'error');
        return;
      }
      seen.add(dbCol);
      data.push({ db_col_nm: dbCol, col_value: colValue, fixed_value: fixedValue, comments });
    }

    // Record-level required information must be present.
    if (!recRawKey || modifiedBy === '' || sessionId === null || sessionId === undefined || String(sessionId).trim() === '' || data.length === 0) {
      this.notify('Unable to save because required information is missing.', 'error');
      return;
    }

    const payload = {
      rec_raw_key: String(recRawKey).trim(),
      status: status,
      modified_by: modifiedBy,
      session_id: String(sessionId).trim(),
      brand_key: this.filterService.baseQuery?.brand_key,
      file_type_dim_key: this.filterService.baseQuery?.file_type_dim_key,
      source_key: this.filterService.baseQuery?.source_key,
      data
    };

    this.isSaving = true;
    this.cdr.detectChanges();

    this.reqSubcription.push(
      this.apiService.saveFixFileOutstandingErrors(payload).subscribe((res: any) => {
        this.isSaving = false;
        this.notify(status === '103' ? 'Record submitted successfully.' : 'Record saved successfully.', 'success');
        // Close only on Submit; keep the popup open after a plain Save.
        if (status === '103') {
          this.closeModal();
        } else {
          // Keep the popup open with the just-saved values intact. Do NOT reload
          // validation details here — that rebuilds modalFields from the stale
          // selectedRow (which still holds the original values), wiping the Fix
          // Record inputs. Instead, persist the saved values locally so the
          // record is treated as "Saved" and can be submitted straight away.
          if (this.selectedRow) {
            this.selectedRow.statusCode = status;
            // Sync the saved fix values back onto the underlying row so a later
            // reopen/navigation and any rebuild reflect the corrected values.
            this.modalFields.forEach(f => {
              const dbCol = String(f?.dbCol ?? '').trim();
              if (dbCol && this.hasRealFixValue(f)) {
                // Store what was actually persisted, so the grid and any later
                // rebuild of the modal reflect the value the API now holds.
                this.selectedRow[dbCol] = this.toPayloadValue(f);
              }
            });
          }
          // Rebase the "modified" baseline to the saved values so there is no
          // false "unsaved changes" prompt and Save disables until further edits,
          // while the Fix Record inputs stay populated for Submit.
          this.modalFields.forEach(f => {
            f.initialFixValue = f.fixValue;
          });
        }
        this.loadApiData();
        this.cdr.detectChanges();
      }, (err: any) => {
        this.isSaving = false;
        const msg = friendlyApiErrorMessage(err, 'Unable to save the record. Please try again.');
        this.notify(msg, 'error');
        this.cdr.detectChanges();
      })
    );
  }

  // True when the open record is still in the Fix stage (status 101) — there is
  // no change history yet, so View Change Log is hidden.
  get isFixRecord(): boolean {
    return Number(this.selectedRow?.['statusCode']) === 101;
  }

  // True when the open record is already saved (status 102) — Save & Next is hidden.
  get isSavedRecord(): boolean {
    return Number(this.selectedRow?.['statusCode']) === 102;
  }

  // True when the open record is already submitted (status 103) — Save & Next
  // and Submit are hidden (view-only).
  get isSubmittedRecord(): boolean {
    return Number(this.selectedRow?.['statusCode']) === 103;
  }

  get totalRecords(): number { return this.filteredRowData.length; }
}
